import React, { useState, useEffect } from 'react'
import { Users, Monitor, Smartphone, Globe, Clock, MapPin, Activity, RefreshCw, LogOut } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'

interface Session {
  user_id: string
  username: string
  display_name: string
  device_type: 'desktop' | 'mobile' | 'tablet'
  browser: string
  ip_address: string
  location?: string
  login_time: string
  last_activity: string
  is_active: boolean
}

export const ActiveSessions: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)

  useEffect(() => {
    loadSessions()

    if (autoRefresh) {
      const interval = setInterval(loadSessions, 10000) // Refresh every 10 seconds
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const loadSessions = async () => {
    try {
      const data = await apiClient.get('/api/session/monitor')

      // Transform the data into our session format
      const activeSessions = data.active_sessions || []
      const formattedSessions: Session[] = activeSessions.map((session: any) => ({
        user_id: session.user_id || session.email,
        username: session.username || session.email?.split('@')[0] || 'Unknown',
        display_name: session.display_name || session.email || 'Unknown User',
        device_type: detectDeviceType(session.user_agent),
        browser: detectBrowser(session.user_agent),
        ip_address: session.ip_address || 'Unknown',
        location: session.location,
        login_time: session.login_time || session.last_seen,
        last_activity: session.last_seen || session.last_activity,
        is_active: session.is_active !== false
      }))

      setSessions(formattedSessions)
    } catch (error) {
      console.error('Failed to load sessions:', error)
      // Don't show error toast if it's just an auth issue
      if (!error?.response?.status || error.response.status !== 401) {
        toast.error('Failed to load active sessions')
      }
      setSessions([])
    } finally {
      setLoading(false)
    }
  }

  const detectDeviceType = (userAgent?: string): 'desktop' | 'mobile' | 'tablet' => {
    if (!userAgent) return 'desktop'
    if (/mobile/i.test(userAgent)) return 'mobile'
    if (/tablet|ipad/i.test(userAgent)) return 'tablet'
    return 'desktop'
  }

  const detectBrowser = (userAgent?: string): string => {
    if (!userAgent) return 'Unknown'
    if (/chrome/i.test(userAgent) && !/edg/i.test(userAgent)) return 'Chrome'
    if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent)) return 'Safari'
    if (/firefox/i.test(userAgent)) return 'Firefox'
    if (/edg/i.test(userAgent)) return 'Edge'
    return 'Other'
  }

  const handleEndSession = async (userId: string) => {
    if (!confirm('Are you sure you want to end this session?')) return

    try {
      await apiClient.post(`/api/session/end/${userId}`)
      toast.success('Session ended successfully')
      loadSessions()
    } catch (error) {
      toast.error('Failed to end session')
    }
  }

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'mobile':
        return <Smartphone className="w-4 h-4" />
      case 'tablet':
        return <Monitor className="w-4 h-4 rotate-90" />
      default:
        return <Monitor className="w-4 h-4" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Active Sessions</h2>
          <p className="text-gray-600">Monitor current user sessions and activity</p>
        </div>
        <div className="flex items-center space-x-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Auto-refresh</span>
          </label>
          <button
            onClick={loadSessions}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Users</p>
              <p className="text-2xl font-bold">{sessions.filter(s => s.is_active).length}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Mobile Sessions</p>
              <p className="text-2xl font-bold">{sessions.filter(s => s.device_type === 'mobile').length}</p>
            </div>
            <Smartphone className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Desktop Sessions</p>
              <p className="text-2xl font-bold">{sessions.filter(s => s.device_type === 'desktop').length}</p>
            </div>
            <Monitor className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Current Sessions</h3>
        </div>

        {sessions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No active sessions found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Login Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map((session, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{session.display_name}</div>
                        <div className="text-xs text-gray-500">@{session.username}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getDeviceIcon(session.device_type)}
                        <span className="ml-2 text-sm">
                          {session.browser} ({session.device_type})
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-3 h-3 mr-1" />
                        {session.location || session.ip_address}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.login_time ?
                        formatDistanceToNow(new Date(session.login_time), { addSuffix: true }) :
                        'Unknown'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {session.last_activity ?
                          formatDistanceToNow(new Date(session.last_activity), { addSuffix: true }) :
                          'Unknown'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {session.is_active ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <Activity className="w-3 h-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-400 text-sm">
                          <Activity className="w-3 h-3 mr-1" />
                          Idle
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleEndSession(session.user_id)}
                        className="text-red-600 hover:text-red-900"
                        title="End Session"
                      >
                        <LogOut className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}